import React from 'react';
import type { DispositionKey, Period, ReasonRow } from '../../dashboard/types';
import { APPOINTMENT_REASONS, dispositionLink, reasonLink } from '../../dashboard/links';
import { DISPOSITIONS, TOO_FEW_THRESHOLD } from '../../dashboard/types';
import { DashCard, DashFooterStrip, DashRowLink, count, money } from './primitives';

/**
 * What ELVA did with each conversation (spec 17 §5.2).
 *
 * **Seven dispositions, and they are the complete set. There is no `Other` row and
 * there must never be one** — every conversation lands in exactly one of the
 * seven. If the back end sends something unmapped, that is a bug to surface, not a
 * bucket to invent.
 */
export function DispositionsCard({ counts, period }: { counts: Record<DispositionKey, number>; period: Period }) {
  const total = DISPOSITIONS.reduce((n, d) => n + counts[d.key], 0);

  // Under 20, a percentage means nothing, so the column is dropped and the counts
  // stay (§10.2). Not the same as hiding the card.
  const showPercent = total >= TOO_FEW_THRESHOLD;

  if (total === 0) {
    return (
      <DashCard title="What ELVA did with each conversation">
        {/* Collapses to one line. No empty bar, no seven rows of 0. */}
        <p className="m-0 px-6 pb-[19px] text-[12.5px] text-[var(--text-muted)]">No conversations to break down.</p>
      </DashCard>
    );
  }

  return (
    <DashCard
      title="What ELVA did with each conversation"
      sub={
        <>
          All {count(total)} of them, and they add up to {count(total)} — there is no <i>other</i> row.
        </>
      }
    >
      {/* The stacked proportion bar. */}
      <div className="px-6">
        <div className="flex h-[10px] w-full overflow-hidden rounded-full bg-[#EDEFF5]">
          {DISPOSITIONS.map((d) => {
            const n = counts[d.key];
            if (n === 0) return null;
            return (
              <span
                key={d.key}
                title={`${d.label} — ${count(n)}`}
                style={{ width: `${String((n / total) * 100)}%`, background: d.colour }}
              />
            );
          })}
        </div>
      </div>

      <div className="mt-3.5">
        {DISPOSITIONS.map((d) => {
          const n = counts[d.key];
          return (
            <DashRowLink key={d.key} href={dispositionLink(period, d.key)}>
              <div className="flex items-center gap-3 px-6 py-[9px]">
                <span className="h-[9px] w-[9px] shrink-0 rounded-full" style={{ background: d.colour }} aria-hidden />
                <span className="min-w-0 flex-1 truncate text-[12.5px] text-[var(--text-secondary)]">{d.label}</span>
                <span className="w-[54px] shrink-0 text-right font-[var(--font-mono)] text-[13px] font-bold text-[var(--text-primary)]">
                  {count(n)}
                </span>
                {showPercent ? (
                  <span className="w-[42px] shrink-0 text-right text-[11.5px] text-[var(--text-muted)]">
                    {Math.round((n / total) * 100)}%
                  </span>
                ) : null}
              </div>
            </DashRowLink>
          );
        })}
      </div>

      {showPercent ? (
        <div className="h-[19px]" />
      ) : (
        <DashFooterStrip>
          Only {count(total)} conversations, so the percentages are withheld — under {TOO_FEW_THRESHOLD} in the
          denominator, a share means nothing. The counts are real.
        </DashFooterStrip>
      )}
    </DashCard>
  );
}

export interface ReasonsCardProps {
  rows: ReasonRow[];
  appointmentsTotal: number;
  productionCents: number;
  period: Period;
}

/**
 * What the appointments were for (§5.3). Ranked **horizontal** bars, descending by
 * booked count (P337).
 *
 * The bar has two segments: solid accent for `booked`, then a pale amber segment
 * for `asked − booked`. **Both scale against the largest `asked` in the set**, so
 * the misses are comparable across rows rather than each row being its own scale.
 *
 * **The miss definition is load-bearing (P338):** `asked` counts requests ELVA
 * could not turn into a booking — no slot inside the rules, or no appointment
 * reason mapped. **A caller who changed their mind is not a miss.** Get this wrong
 * and the number becomes an unfalsifiable complaint about patients rather than
 * something a practice can fix.
 */
export function ReasonsCard({ rows, period }: ReasonsCardProps) {
  const ranked = [...rows].sort((a, b) => b.booked - a.booked);
  const totalMisses = ranked.reduce((n, r) => n + (r.asked - r.booked), 0);
  const maxAsked = Math.max(1, ...ranked.map((r) => r.asked));
  const bookedTotal = ranked.reduce((n, r) => n + r.booked, 0);

  if (ranked.length === 0) {
    return (
      <DashCard title="What the appointments were for">
        <p className="m-0 px-6 pb-[19px] text-[12.5px] text-[var(--text-muted)]">
          Nothing was booked, so there is nothing to rank.
        </p>
      </DashCard>
    );
  }

  /** Under 5 appointments the list shows and the ranking does not — three bars is
   * not a ranking (§10.2). */
  const ranks = bookedTotal >= 5;

  return (
    <DashCard
      title="What the appointments were for"
      sub={
        ranks ? (
          <>
            Ranked by how many.{' '}
            <b className="font-semibold text-[var(--text-secondary)]">
              The pale end of a bar is what was asked for and ELVA couldn’t book.
            </b>
          </>
        ) : (
          <>
            Only {count(bookedTotal)} appointments, so this is the list and not a ranking — three bars is not a ranking.
          </>
        )
      }
      headRight={
        totalMisses > 0 ? (
          <>
            <div className="font-[var(--font-mono)] text-[20px] font-bold text-[var(--warn-amber)]">
              {count(totalMisses)}
            </div>
            <div className="text-[11px] text-[var(--text-muted)]">asked for, not booked</div>
          </>
        ) : null
      }
    >
      <div>
        {ranked.map((r) => {
          const missed = Math.max(0, r.asked - r.booked);
          return (
            <DashRowLink key={r.reason} href={reasonLink(period, r.reason)}>
              <div className="flex items-center gap-3 px-6 py-[9px]">
                <span
                  className="w-[178px] shrink-0 truncate text-[12.5px] font-medium text-[var(--text-primary)]"
                  title={r.reason}
                >
                  {r.reason}
                </span>
                <span className="flex h-[9px] min-w-0 flex-1 items-stretch overflow-hidden rounded-full bg-[#F1F3F9]">
                  <span
                    style={{ width: `${String((r.booked / maxAsked) * 100)}%`, background: 'var(--brand-indigo)' }}
                  />
                  {missed > 0 ? (
                    <span
                      title={`${count(missed)} asked for and could not be booked`}
                      style={{ width: `${String((missed / maxAsked) * 100)}%`, background: '#F2DCAE' }}
                    />
                  ) : null}
                </span>
                <span className="w-[40px] shrink-0 text-right font-[var(--font-mono)] text-[13px] font-bold text-[var(--text-primary)]">
                  {count(r.booked)}
                </span>
                <span className="w-[34px] shrink-0 text-right font-[var(--font-mono)] text-[11.5px] font-bold text-[var(--warn-amber)]">
                  {missed > 0 ? `+${count(missed)}` : ''}
                </span>
                <span className="w-[72px] shrink-0 text-right font-[var(--font-mono)] text-[12px] font-semibold text-[var(--money-green)]">
                  {money(r.valueCents)}
                </span>
              </div>
            </DashRowLink>
          );
        })}
      </div>

      {/* The two identities this table IS the source of (§7.3, §7.4, P339). */}
      <DashFooterStrip>
        <span className="flex flex-wrap items-end justify-end gap-3">
          <a
            href={APPOINTMENT_REASONS}
            className="shrink-0 text-[13px] font-semibold text-[var(--brand-indigo)] hover:underline"
          >
            Appointment reasons →
          </a>
        </span>
      </DashFooterStrip>
    </DashCard>
  );
}
